import ComponentConfig from './component-config';

export default class BaseComponent {
    constructor(element, id = null) {
        this.id = id;

        this._isDestroyed = false;
        this.element = element;
        this.options = {};
        this.refs = {};
        this.instanceId = null;
        this.listenersList = [];

        if (this.hasId()) {
            this.initInstanceId();
            this.initOptions();
            this.initRefs();
        }

        this.checkRequiredRefs(this.refs);
    }

    hasId() {
        return this.id !== null;
    }

    static getRequiredRefs() {
        return [];
    }

    initOptions() {
        let options = {};
        const datasetOptionsProperty = ComponentConfig.getDatasetOptionsProperty(this.id);
        if (this.element &&
            this.element.dataset &&
            (datasetOptionsProperty in this.element.dataset)) {
            try {
                options = JSON.parse(this.element.dataset[datasetOptionsProperty]);
            } catch (error) {
                console.error(`Unable to parse options from ${datasetOptionsProperty} on element:`, this.element);
                throw error;
            }
        }
        this.options = options;
    }

    initRefs() {
        const refs = {};
        const selector = `[${ComponentConfig.getRefProperty(this.id)}]`;
        const refElements = this.querySelectorAll(selector);
        if (refElements && refElements.length) {
            refElements.forEach((element) => {
                const datasetRefProperty = ComponentConfig.getDatasetRefProperty(this.id);
                if (element.dataset && (datasetRefProperty in element.dataset)) {
                    ComponentConfig.insertToCollection(refs, element.dataset[datasetRefProperty], element);
                }
            });
        }
        this.refs = refs;
    }

    checkRequiredRefs(refs) {
        const requiredRefs = this.constructor.getRequiredRefs();
        if (requiredRefs && requiredRefs.length) {
            requiredRefs.forEach((name) => {
                if (refs[name] === undefined) {
                    throw new Error(`the value of required ref ${name} is ${refs[name]}`);
                }
            });
        }
    }

    destroy() {
        this.removeListeners();
        this.onDestroy();
        this._isDestroyed = true;
    }

    isDestroyed() {
        return this._isDestroyed;
    }

    onDestroy() {
        // hook for children
    }

    initInstanceId() {
        this.instanceId = this.getNodeInstanceId(this.element, true);
    }

    getNodeInstanceId(node, component = false) {
        let result = null;

        let instanceIdDatasetProperty;
        if (component) {
            // we don't need id for root node
            instanceIdDatasetProperty = ComponentConfig.getDatasetComponentInstanceIdProperty();
        } else {
            instanceIdDatasetProperty = ComponentConfig.getDatasetInstanceIdProperty(this.id);
        }

        if (node &&
            node.dataset &&
            (instanceIdDatasetProperty in node.dataset)) {
            const instanceId = node.dataset[instanceIdDatasetProperty];
            if (instanceId) {
                result = instanceId;
            }
        }

        return result;
    }

    getScopedAttribute(attributeName) {
        return ComponentConfig.getAttributeProperty(this.id, attributeName);
    }

    getAttribute(node, attributeName) {
        if (!this.hasId()) {
            throw new Error('Id isn\'t specified');
        }

        let result = null;

        if (this.instanceId) {
            const nodeInstanceId = this.getNodeInstanceId(node);
            if (nodeInstanceId === null || this.instanceId !== nodeInstanceId) {
                return null;
            }
        }

        const attributeDatasetProperty = ComponentConfig.getDatasetAttributeProperty(this.id, attributeName);
        if (node.dataset && attributeDatasetProperty in node.dataset) {
            result = node.dataset[attributeDatasetProperty];
        }

        return result;
    }

    removeAttribute(node, attributeName) {
        if (!this.hasId()) {
            throw new Error('Id isn\'t specified');
        }

        if (this.instanceId) {
            const nodeInstanceId = this.getNodeInstanceId(node);
            if (nodeInstanceId === null || this.instanceId !== nodeInstanceId) {
                return null;
            }
        }

        const attributeDatasetProperty = ComponentConfig.getDatasetAttributeProperty(this.id, attributeName);
        if (node.dataset && attributeDatasetProperty in node.dataset) {
            delete node.dataset[attributeDatasetProperty];
        }

        return null;
    }

    hasAttribute(node, attributeName) {
        return this.getAttribute(node, attributeName) !== null;
    }

    querySelectorAll(selector) {
        // if instance id is presented we find only refs with that id as well
        if (this.instanceId) {
            selector += `[${ComponentConfig.getInstanceIdProperty(this.id)}="${this.instanceId}"]`;
        }

        if (this.element) {
            return [...this.element.querySelectorAll(selector)];
        }

        return null;
    }

    findChildrenWithAttribute(attributeName) {
        return this.querySelectorAll(`[${ComponentConfig.getAttributeProperty(this.id, attributeName)}]`);
    }

    addListener(elem, eventName, eventCallback) {
        if (!elem || typeof elem.addEventListener !== 'function') {
            throw new Error('You must provide valid `node` element');
        }

        if (!eventName && typeof eventName !== 'string') {
            throw new Error('You must provide valid `event` name');
        }

        if (!eventCallback && typeof eventCallback !== 'function') {
            throw new Error(' You must provide function as callback');
        }

        elem.addEventListener(eventName, eventCallback);
        this.listenersList.push({
            elem,
            eventName,
            eventCallback
        });
    }

    removeListeners() {
        this.listenersList.forEach((listener) => {
            const { elem, eventName, eventCallback } = listener;

            elem.removeEventListener(eventName, eventCallback);
        });

        this.listenersList = [];
    }
}
